import { assert } from '@ember/debug';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import type { PowerSelectTriggerSignature } from 'ember-power-select/components/power-select/trigger';
import type { Select } from 'ember-power-select/components/power-select-multiple';
import { emberPowerSelectIsSelected } from 'ember-power-select/helpers/ember-power-select-is-selected';

import type {
  Group,
  Option,
} from '../../../types/components/prism/combo-select-input.ts';
import PrismIcon from '../icon.gts';

function areAllOptionsSelected(
  selectedOptions: unknown[],
  allOptions: (Option | Group<Option>)[],
): boolean {
  return allOptions.every((optionOrGroup) => {
    if ('options' in optionOrGroup) {
      // If it's a group, check all options in the group
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return areAllOptionsSelected(selectedOptions, optionOrGroup.options);
    }
    // If it's an option, check if it's selected
    return emberPowerSelectIsSelected([optionOrGroup, selectedOptions]);
  });
}

interface PrismComboSelectInputMultipleSelectionTriggerArgs {
  buildLabelWithCount: (count: number) => string;
  isSmall?: true;
  selectAllLabel?: string;
}

interface PrismComboSelectInputMultipleSelectionTriggerSignature {
  Args: PowerSelectTriggerSignature<Option, Group<Option>>['Args'] &
    PrismComboSelectInputMultipleSelectionTriggerArgs;
}

export default class PrismComboSelectInputMultipleSelectionTrigger extends Component<PrismComboSelectInputMultipleSelectionTriggerSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get label() {
    const { selected, options } = this.select;
    const count = selected.length;

    if (count === 1) {
      return (selected[0] as Option).label;
    }

    // When all options are selected we show the "selectAllLabel" in the trigger
    if (this.args.selectAllLabel && areAllOptionsSelected(selected, options)) {
      return this.args.selectAllLabel;
    }

    return this.args.buildLabelWithCount(count);
  }

  get labelFontClass(): string {
    return this.args.isSmall ? 'text-200--medium' : 'text-300--medium';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get select() {
    return this.args.select as Select<Option, Group<Option>>;
  }

  constructor(
    owner: Owner,
    args: PrismComboSelectInputMultipleSelectionTriggerSignature['Args'],
  ) {
    super(owner, args);

    assert(
      'buildLabelWithCount function is required when using a multiple select',
      typeof this.args.buildLabelWithCount === 'function',
    );
  }

  <template>
    <p class="trigger__label {{this.labelFontClass}}">
      {{this.label}}
    </p>
    {{#if this.select.isOpen}}
      <PrismIcon @name="chevron-up" @size="small" />
    {{else}}
      <PrismIcon @name="chevron-down" @size="small" />
    {{/if}}
  </template>
}
